/**
 * Links (W1-10b core-commands-motion; DESIGN.md §4.7, §5.6.4, §5.6.5; rpe-editor-features.md E§4.5, E§4.14).
 *
 * Link groups (RPE binding group, EVT-21): segments with the same `Key.link` (!= 0) share their start value
 * (the start key's vOut), end value (the next key's vIn) and curve; times stay independent. `syncLinkGroups`
 * writes a primary segment's values and curve to every other member after an edit; `linkSegments` assigns a fresh
 * group; operations that change a segment's shape on their own (split, bake, join, reverse, merge on delete) take
 * the segment out of its group, and `cleanLinks` dissolves groups that are left with a single segment.
 *
 * Position X/Y link (`LineEditorData.xyLinked`): inserting, moving, deleting, splitting or re-easing a key on one
 * axis does the same on the other axis at the same beat (same layer); values stay independent. The helpers here
 * find the partner track and the partner keys of a selection (layers.ts and transform.ts mirror span operations,
 * moving segments to a layer and removing time, with the same partner rule). `alignXyBeats` re-establishes shared
 * beats after a paste replaced keys of one or both axes inside some spans: each axis gets a shape-preserving key at
 * every beat the other one has there (and at beats outside the spans that an axis gained, such as a lead-in key at
 * beat 0 or the pieces of a baked edge split). `alignXyAll` does the same over all keys of both axes (a created
 * axis, a raw event replacement, a reverse split at tempo changes). An absent axis is a constant 0: when it has to
 * be created it gets a key of 0 at every beat of the other axis (`zeroAxisKeys`), which is exact.
 */

import { Ease, LINEAR } from '../ease/index';
import { valueEquals } from '../ops/equal';
import { Beats } from '../time/beat';
import type { Beat } from '../time/types';
import type { TxBuilder } from '../ops/types';
import type { ChartDoc, KeyId, LineId, LineProp, TrackId } from '../model/types';
import {
  commitKeys,
  createTrack,
  easeUseFor,
  indexAt,
  indexOfId,
  newRec,
  ownerInfo,
  readKeys,
  sameValue,
  setArrive,
  trackInfo,
  type KeyRec,
  type MotionView,
  type TrackInfo,
} from './motionTrack';
import { collectSplit, worseSplitOn, type SplitOn } from './motionNotes';
import { explicitLeadIn } from './motionTime';
import { curveCtx, EXACT_SPLIT, insertKeyRec, worseSplit, type CurveCtx, type SplitReport } from './splitKey';

// ---------------------------------------------------------------------------------------------------------------
// Link groups
// ---------------------------------------------------------------------------------------------------------------

/** A link group id no key uses yet. */
export function nextLinkId(doc: ChartDoc): number {
  let max = 0;
  for (const k of doc.keys.values()) if (Math.abs(k.link) > max) max = Math.abs(k.link);
  return max + 1;
}

/** True when the key starts a segment (it is not the last key of its track). */
export function startsSegment(view: MotionView, id: KeyId): boolean {
  return view.idx.nextKey(id) !== undefined;
}

/** Dissolves the given groups when at most one segment is left in them. */
export function cleanLinks(tx: TxBuilder, links: Iterable<number>): void {
  for (const link of new Set(links)) {
    if (link === 0) continue;
    const members = [...tx.idx.linkMembers(link)];
    const segments = members.filter((id) => startsSegment(tx, id));
    if (segments.length > 1) continue;
    for (const id of members) tx.update('keys', id, { link: 0 });
  }
}

/**
 * Writes the values and curve of each primary segment (start key id) to the other members of its link group.
 * Members whose track cannot take the curve (e.g. a Bezier on scroll speed) keep their own curve. A member that is
 * the last segment of its track and ended without a jump keeps holding its (new) end value afterwards. One primary
 * per group: the first one listed wins.
 */
export function syncLinkGroups(tx: TxBuilder, primaries: Iterable<KeyId>): void {
  const done = new Set<number>();
  for (const pid of primaries) {
    const p = tx.doc.keys.get(pid);
    if (p === undefined || p.link === 0 || done.has(p.link)) continue;
    const nextId = tx.idx.nextKey(pid);
    const next = nextId === undefined ? undefined : tx.doc.keys.get(nextId);
    if (next === undefined) continue;
    done.add(p.link);
    const pInfo = trackInfo(tx, p.track);
    if (pInfo === null) continue;
    for (const mid of [...tx.idx.linkMembers(p.link)]) {
      if (mid === pid) continue;
      const m = tx.doc.keys.get(mid);
      if (m === undefined) continue;
      const info = trackInfo(tx, m.track);
      if (info === null || info.valueType !== pInfo.valueType) continue;
      const keys = readKeys(tx, m.track);
      const i = indexOfId(keys, mid);
      const k = keys[i];
      const after = keys[i + 1];
      if (k === undefined || after === undefined) continue;
      const changed = !sameValue(k.vOut, p.vOut) || !sameValue(after.vIn, next.vIn) || !valueEquals(k.ease, p.ease);
      if (!changed) continue;
      // Continuity (UX-033): a member key without a jump moves as a whole, as the primary's own keys do, unless its
      // other side belongs to the same group (adjacent members share that key: there the values may jump) or follows
      // a Stay (the hold invariant keeps the stayed value).
      const before = keys[i - 1];
      const startFollows =
        i > 0 &&
        sameValue(k.vIn, k.vOut) &&
        before !== undefined &&
        before.link !== p.link &&
        before.ease.kind !== 'hold';
      k.vOut = p.vOut;
      if (i === 0 || startFollows) k.vIn = p.vOut;
      const endFollows = sameValue(after.vIn, after.vOut) && after.link !== p.link && keys[i + 2] !== undefined;
      if (endFollows) {
        after.vIn = next.vIn;
        after.vOut = next.vIn;
      } else {
        // The member's end: a last key without a jump holds the new end afterwards.
        setArrive(keys, i + 1, next.vIn);
      }
      if (Ease.validateFor(p.ease, easeUseFor(tx, info, p.vOut, next.vIn)) === null) k.ease = p.ease;
      commitKeys(tx, info, keys);
    }
  }
}

/**
 * The segments (start key ids) whose values a change of key `id` touched: its own outgoing segment and the
 * segment arriving at it, when they are linked.
 */
export function linkedNeighbours(view: MotionView, id: KeyId): KeyId[] {
  const out: KeyId[] = [];
  const k = view.doc.keys.get(id);
  if (k === undefined) return out;
  if (k.link !== 0 && startsSegment(view, id)) out.push(id);
  const prev = view.idx.prevKey(id);
  if (prev !== undefined && (view.doc.keys.get(prev)?.link ?? 0) !== 0) out.push(prev);
  return out;
}

// ---------------------------------------------------------------------------------------------------------------
// Position X/Y link
// ---------------------------------------------------------------------------------------------------------------

export interface XyPartner {
  readonly line: LineId;
  readonly prop: LineProp;
  readonly layer: number;
}

/** The other axis of a position track on a line with X/Y linked, or null. */
export function xyPartnerOf(info: TrackInfo): XyPartner | null {
  if (info.line === null || !info.line.editor.xyLinked) return null;
  if (info.prop === 'moveX') return { line: info.line.id, prop: 'moveY', layer: info.layer };
  if (info.prop === 'moveY') return { line: info.line.id, prop: 'moveX', layer: info.layer };
  return null;
}

export function xyPartnerTrack(view: MotionView, info: TrackInfo): TrackId | undefined {
  const p = xyPartnerOf(info);
  return p === null ? undefined : view.idx.track(p.line, p.prop, p.layer);
}

/** Key of `track` at exactly beat t, if any. */
export function keyAtBeat(view: MotionView, track: TrackId, t: Beat): KeyId | undefined {
  const ids = view.idx.keysByTrack(track);
  let lo = 0;
  let hi = ids.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const id = ids[mid] as KeyId;
    const k = view.doc.keys.get(id);
    if (k === undefined) return undefined;
    const c = Beats.cmp(k.t, t);
    if (c === 0) return id;
    if (c < 0) lo = mid + 1;
    else hi = mid - 1;
  }
  return undefined;
}

/**
 * `ids` plus, for keys on X/Y-linked position tracks, the partner axis' keys at the same beats (only keys that
 * start a segment when `segments` is true). Order: the given ids first.
 */
export function withXyPartners(view: MotionView, ids: readonly KeyId[], segments = false): KeyId[] {
  const out: KeyId[] = [];
  const seen = new Set<KeyId>();
  const push = (id: KeyId): void => {
    if (!seen.has(id)) {
      seen.add(id);
      out.push(id);
    }
  };
  for (const id of ids) push(id);
  for (const id of ids) {
    const k = view.doc.keys.get(id);
    if (k === undefined) continue;
    const info = trackInfo(view, k.track);
    if (info === null) continue;
    const partner = xyPartnerTrack(view, info);
    if (partner === undefined) continue;
    const pk = keyAtBeat(view, partner, k.t);
    if (pk === undefined) continue;
    if (segments && !startsSegment(view, pk)) continue;
    push(pk);
  }
  return out;
}

/**
 * Where an operation changed the keys of the position axes of one layer of an X/Y-linked line; `alignXyBeats` makes
 * both axes share their beats there.
 */
export interface XyWindow {
  readonly line: LineId;
  readonly layer: number;
  /** Closed beat spans whose keys were replaced. */
  readonly spans: [Beat, Beat][];
  /** Beats outside the spans that an axis gained (a lead-in key at beat 0, pieces of a baked edge split). */
  readonly beats: Map<string, Beat>;
  /** The axes the operation edited; splits on the other axis are partner splits (their own note). */
  readonly edited: Set<LineProp>;
}

/** The splits `alignXyBeats` made: the worst one on an edited axis, and the worst one on a partner axis. */
export interface XyAlignReport {
  readonly edited: SplitOn | null;
  readonly partner: SplitReport;
}

/** Rounds of mirroring after which `alignXyBeats` stops (baked splits can add beats that need mirroring again). */
const XY_ALIGN_ROUNDS = 8;

interface XyAxis {
  readonly prop: LineProp;
  readonly info: TrackInfo;
  readonly exists: boolean;
  readonly keys: KeyRec[];
  readonly ctx: CurveCtx;
  touched: boolean;
  /** A created axis next to an imported lead-in: it is flagged too (its constant 0 extrapolates to 0). */
  imported: boolean;
  report: SplitReport;
}

/**
 * Keys of an absent position axis created next to the other axis of an X/Y-linked line: a constant 0 (what the
 * absent axis was) with a key at every beat of the other axis, so both axes share their beats. Straight segments,
 * so a value set on one of these keys later glides in and out like any new key.
 */
export function zeroAxisKeys(other: readonly KeyRec[]): KeyRec[] {
  return other.map((k) => newRec(k.t, 0, 0, LINEAR));
}

/**
 * Gives both position axes of `win.layer` a key at every beat inside the window (`win.spans`, `win.beats`) that
 * either axis has (shape-preserving splits, an extrapolated lead-in made explicit first). An absent axis is created
 * as a constant 0 with a key at every beat of the other axis (`zeroAxisKeys`; exact; flagged as an imported lead-in
 * when the other axis is one, so the other axis needs no key at beat 0). Beats an insertion adds (the pieces of a
 * baked split, a lead-in key) are mirrored in the next round. Writes the changed axes.
 */
export function alignXyBeats(tx: TxBuilder, win: XyWindow, unlinked: Set<number>): XyAlignReport {
  const none: XyAlignReport = { edited: null, partner: EXACT_SPLIT };
  if (tx.doc.lines.get(win.line)?.editor.xyLinked !== true) return none;
  const axes: XyAxis[] = [];
  for (const prop of ['moveX', 'moveY'] as const) {
    const found = ownerInfo(tx, { kind: 'line', line: win.line, prop, layer: win.layer });
    if (found === null) return none;
    axes.push({
      prop,
      info: found.info,
      exists: found.exists,
      keys: found.exists ? readKeys(tx, found.info.track.id) : [],
      ctx: curveCtx(tx, found.info),
      touched: false,
      imported: false,
      report: EXACT_SPLIT,
    });
  }
  for (const [a, other] of [
    [axes[0], axes[1]],
    [axes[1], axes[0]],
  ] as const) {
    if (a === undefined || other === undefined || a.exists || !other.exists || other.keys.length === 0) continue;
    a.keys.push(...zeroAxisKeys(other.keys));
    const first = other.keys[0] as KeyRec;
    a.imported = other.info.track.importedLeadIn === true && Beats.lt(Beats.ZERO, first.t);
    a.touched = true;
  }
  const inWindow = (b: Beat): boolean =>
    win.beats.has(Beats.key(b)) || win.spans.some(([s, e]) => Beats.lte(s, b) && Beats.lte(b, e));
  for (let round = 0; round < XY_ALIGN_ROUNDS; round++) {
    const wanted = new Map<string, Beat>();
    for (const a of axes) {
      if (!a.exists && !a.touched) continue;
      for (const k of a.keys) if (inWindow(k.t)) wanted.set(Beats.key(k.t), k.t);
    }
    let changed = false;
    for (const a of axes) {
      if (!a.exists && !a.touched) continue;
      const missing = [...wanted.values()].filter((b) => indexAt(a.keys, b) < 0).sort((x, y) => Beats.cmp(x, y));
      const lowest = missing[0];
      if (lowest === undefined) continue;
      const before = new Set(a.keys.map((k) => Beats.key(k.t)));
      a.touched = true;
      a.report = worseSplit(a.report, explicitLeadIn(a.keys, lowest, a.ctx, unlinked));
      for (const b of missing) a.report = worseSplit(a.report, insertKeyRec(a.keys, b, a.ctx, unlinked));
      for (const k of a.keys) if (!before.has(Beats.key(k.t))) win.beats.set(Beats.key(k.t), k.t);
      changed = true;
    }
    if (!changed) break;
  }
  let edited: SplitOn | null = null;
  let partner: SplitReport = EXACT_SPLIT;
  for (const a of axes) {
    if (!a.touched) continue;
    const info = a.exists ? a.info : createTrack(tx, a.info.track.owner);
    if (!a.exists && a.imported && Beats.lt(Beats.ZERO, (a.keys[0] as KeyRec).t)) {
      tx.update('tracks', info.track.id, { importedLeadIn: true });
    }
    commitKeys(tx, info, a.keys);
    if (win.edited.has(a.prop)) edited = collectSplit(edited, info, a.report);
    else partner = worseSplit(partner, a.report);
  }
  return { edited, partner };
}

/**
 * `alignXyBeats` over every key of both position axes of a layer: afterwards the axes have keys at exactly the same
 * beats (an absent axis is created as a constant 0 when the other one exists). `edited`: the axes the command
 * changed (their splits are noted as the command's own, the other axis' as partner splits).
 */
export function alignXyAll(
  tx: TxBuilder,
  line: LineId,
  layer: number,
  edited: Iterable<LineProp>,
  unlinked: Set<number>,
): XyAlignReport {
  let lo: Beat | null = null;
  let hi: Beat | null = null;
  for (const prop of ['moveX', 'moveY'] as const) {
    const id = tx.idx.track(line, prop, layer);
    if (id === undefined) continue;
    const ids = tx.idx.keysByTrack(id);
    const first = ids[0] === undefined ? undefined : tx.doc.keys.get(ids[0]);
    const last = ids[ids.length - 1] === undefined ? undefined : tx.doc.keys.get(ids[ids.length - 1] as KeyId);
    if (first !== undefined && (lo === null || Beats.lt(first.t, lo))) lo = first.t;
    if (last !== undefined && (hi === null || Beats.lt(hi, last.t))) hi = last.t;
  }
  if (lo === null || hi === null) return { edited: null, partner: EXACT_SPLIT };
  return alignXyBeats(tx, { line, layer, spans: [[lo, hi]], beats: new Map(), edited: new Set(edited) }, unlinked);
}

/**
 * Collects the windows of X/Y-linked lines whose position axes an operation changed inside [s, e], when both axes
 * exist (`alignXyBeats` then gives both axes the keys the other one gained there, e.g. splits at tempo changes).
 */
export function noteXyWindow(tx: TxBuilder, windows: Map<string, XyWindow>, info: TrackInfo, s: Beat, e: Beat): void {
  if (info.line === null || info.prop === null || xyPartnerTrack(tx, info) === undefined) return;
  const key = `${String(info.line.id)}:${String(info.layer)}`;
  let w = windows.get(key);
  if (w === undefined) {
    w = { line: info.line.id, layer: info.layer, spans: [], beats: new Map(), edited: new Set() };
    windows.set(key, w);
  }
  w.spans.push([s, e]);
  w.edited.add(info.prop);
}

/** Aligns the collected X/Y windows (see `noteXyWindow`); returns the worst split of an edited axis and of a partner. */
export function alignXyWindows(
  tx: TxBuilder,
  windows: ReadonlyMap<string, XyWindow>,
  unlinked: Set<number>,
): { readonly edited: SplitOn | null; readonly partner: SplitReport } {
  let edited: SplitOn | null = null;
  let partner: SplitReport = EXACT_SPLIT;
  for (const w of windows.values()) {
    const aligned = alignXyBeats(tx, w, unlinked);
    edited = worseSplitOn(edited, aligned.edited);
    partner = worseSplit(partner, aligned.partner);
  }
  return { edited, partner };
}
